import {
  type IMCAttributionWindowDays,
  type INBAccountingMode,
} from "@north-beam/nb-common";

import { type SalesMetricV2 } from "../types";

export function makeRoas({
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "roas";

  return {
    id,
    name: isDashboardV2
      ? "ROAS"
      : accountingMode === "accrual"
      ? "ROAS"
      : "MER",
    fullName:
      "ROAS / All" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description: "Return on ad spend for all revenue. Spend ÷ all revenue.",
    category: "northbeam",
    positiveTrend: "up",
    format: "multiplier",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeRoasFt({
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "roasFt";

  return {
    id,
    name: isDashboardV2
      ? "ROAS (New)"
      : accountingMode === "accrual"
      ? "ROAS (1st time)"
      : "MER (1st time)",
    fullName:
      "ROAS / New" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "Return on ad spend for new customers. Spend ÷ first order revenue.",
    category: "northbeam",
    positiveTrend: "up",
    format: "multiplier",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeRoasRtn({
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "roasRtn";

  return {
    id,
    name: isDashboardV2
      ? "ROAS (Returning)"
      : accountingMode === "accrual"
      ? "ROAS (Returning)"
      : "MER (Returning)",
    fullName:
      "ROAS / Ret" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "Return on ad spend for returning customers. Spend ÷ 2nd+ order revenue.",
    category: "northbeam",
    positiveTrend: "up",
    format: "multiplier",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeRoasLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "roasLtv";

  return {
    id,
    name: isDashboardV2 ? "ROAS (∞)" : "LTV ROAS",
    fullName: "ROAS / All (∞)",
    description: "Return on Ad Spend - computed as Rev ÷ Spend",
    category: "northbeam",
    positiveTrend: "up",
    format: "multiplier",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeRoasFtLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "roasFtLtv";

  return {
    id,
    name: isDashboardV2 ? "ROAS (∞, New)" : "LTV ROAS (1st time)",
    fullName: "ROAS / New (∞)",
    description: "Return on Ad Spend - computed as Rev ÷ Spend",
    category: "northbeam",
    positiveTrend: "up",
    format: "multiplier",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeRoasRtnLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "roasRtnLtv";

  return {
    id,
    name: isDashboardV2 ? "ROAS (∞, Returning)" : "LTV ROAS (Returning)",
    fullName: "ROAS / Ret (∞)",
    description: "Return on Ad Spend - computed as Rev ÷ Spend",
    category: "northbeam",
    positiveTrend: "up",
    format: "multiplier",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeCac({
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "cac";

  return {
    id,
    name: isDashboardV2 ? "CPO" : "CAC",
    fullName:
      "CPO / All" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description: isDashboardV2
      ? "Cost Per Order"
      : "Cost to acquire a customer - calculated as Spend ÷ Txns",
    category: "northbeam",
    positiveTrend: "down",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeCacFt({
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "cacFt";

  return {
    id,
    name: isDashboardV2 ? "CPO (New)" : "CAC (1st time)",
    fullName:
      "CPO / New" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description: isDashboardV2
      ? "Cost Per Order"
      : "Cost to acquire a customer - calculated as Spend ÷ Txns",
    category: "northbeam",
    positiveTrend: "down",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeCacRtn({
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "cacRtn";

  return {
    id,
    name: isDashboardV2 ? "CPO (Returning)" : "CAC (Returning)",
    fullName:
      "CPO / Ret" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description: isDashboardV2
      ? "Cost Per Order"
      : "Cost to acquire a customer - calculated as Spend ÷ Txns",
    category: "northbeam",
    positiveTrend: "down",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeCacLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "cacLtv";

  return {
    id,
    name: isDashboardV2 ? "CPO (∞)" : "LTV CAC",
    fullName: "CPO / All (∞)",
    description: isDashboardV2
      ? "Cost Per Order"
      : "Cost to acquire a customer - calculated as Spend ÷ Txns",
    category: "northbeam",
    positiveTrend: "down",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeCacFtLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "cacFtLtv";

  return {
    id,
    name: isDashboardV2 ? "CPO (∞, New)" : "LTV CAC (1st time)",
    fullName: "CPO / New (∞)",
    description: isDashboardV2
      ? "Cost Per Order"
      : "Cost to acquire a customer - calculated as Spend ÷ Txns",
    category: "northbeam",
    positiveTrend: "down",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeCacRtnLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "cacRtnLtv";

  return {
    id,
    name: isDashboardV2 ? "CPO (∞, Returning)" : "LTV CAC (Returning)",
    fullName: "CPO / Ret (∞)",
    description: isDashboardV2
      ? "Cost Per Order"
      : "Cost to acquire a customer - calculated as Spend ÷ Txns",
    category: "northbeam",
    positiveTrend: "down",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeAov({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "aov";

  return {
    id,
    name: "AOV",
    fullName:
      "AOV / All" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description: "Average Order Value - computed as Revenue / Transactions",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeAovFt({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "aovFt";

  return {
    id,
    name: "AOV (New)",
    fullName:
      "AOV / New" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description: "Average Order Value - computed as Revenue / Transactions",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeAovRtn({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "aovRtn";

  return {
    id,
    name: "AOV (Returning)",
    fullName:
      "AOV / Ret" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description: "Average Order Value - computed as Revenue / Transactions",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeAovLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "aovLtv";

  return {
    id,
    name: isDashboardV2 ? "AOV (∞)" : "LTV AOV",
    fullName: "AOV / All (∞)",
    description: "Average Order Value - computed as Revenue / Transactions",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeAovFtLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "aovFtLtv";

  return {
    id,
    name: isDashboardV2 ? "AOV (∞, New)" : "LTV AOV (1st time)",
    fullName: "AOV / New (∞)",
    description: "Average Order Value - computed as Revenue / Transactions",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeAovRtnLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "aovRtnLtv";

  return {
    id,
    name: isDashboardV2 ? "AOV (∞, Returning)" : "LTV AOV (Returning)",
    fullName: "AOV / Ret (∞)",
    description: "Average Order Value - computed as Revenue / Transactions",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeEcr({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "ecr";

  return {
    id,
    name: "ECR",
    fullName:
      "ECR / All" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description: "Ecommerce conversion rate - calculated as Txns ÷ Visits",
    category: "northbeam",
    positiveTrend: "up",
    format: "percentage",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeEcrFt({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "ecrFt";

  return {
    id,
    name: "ECR (New)",
    fullName:
      "ECR / New" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description: "Ecommerce conversion rate - calculated as Txns ÷ Visits",
    category: "northbeam",
    positiveTrend: "up",
    format: "percentage",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeEcrRtn({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "ecrRtn";

  return {
    id,
    name: "ECR (Returning)",
    fullName:
      "ECR / Ret" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description: "Ecommerce conversion rate - calculated as Txns ÷ Visits",
    category: "northbeam",
    positiveTrend: "up",
    format: "percentage",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeEcrLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "ecrLtv";

  return {
    id,
    name: isDashboardV2 ? "ECR (∞)" : "LTV ECR",
    fullName: "ECR / All (∞)",
    description: "Ecommerce conversion rate - calculated as Txns ÷ Visits",
    category: "northbeam",
    positiveTrend: "up",
    format: "percentage",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeEcrFtLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "ecrFtLtv";

  return {
    id,
    name: isDashboardV2 ? "ECR (∞, New)" : "LTV ECR (1st time)",
    fullName: "ECR / New (∞)",
    description: "Ecommerce conversion rate - calculated as Txns ÷ Visits",
    category: "northbeam",
    positiveTrend: "up",
    format: "percentage",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeEcrRtnLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "ecrRtnLtv";

  return {
    id,
    name: isDashboardV2 ? "ECR (∞, Returning)" : "LTV ECR (Returning)",
    fullName: "ECR / Ret (∞)",
    description: "Ecommerce conversion rate - calculated as Txns ÷ Visits",
    category: "northbeam",
    positiveTrend: "up",
    format: "percentage",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makePercentageNewVisits(): SalesMetricV2 {
  const id = "percentageNewVisits";
  const name = "% New Visits";

  return {
    id,
    name,
    fullName: name,
    description:
      "Percentage of visits to your website that are from new visitors, as determined by " +
      "the Northbeam Pixel",
    category: "northbeam",
    positiveTrend: "up",
    format: "percentage",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeCpm(): SalesMetricV2 {
  const id = "cpm";
  const name = "CPM";

  return {
    id,
    name,
    fullName: name,
    description: "Cost Per Mille - computed as 1,000 × Spend ÷ Impressions",
    category: "northbeam",
    positiveTrend: "down",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeCtr(): SalesMetricV2 {
  const id = "ctr";
  const name = "CTR";

  return {
    id,
    name,
    fullName: name,
    description: "Click through rate - calculated as Visits ÷ Imprs",
    category: "northbeam",
    positiveTrend: "up",
    format: "percentage",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeEcpc(): SalesMetricV2 {
  const id = "ecpc";
  const name = "eCPC";

  return {
    id,
    name,
    fullName: name,
    description: "Effective cost per click - calculated as Spend ÷ Visits",
    category: "northbeam",
    positiveTrend: "down",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeEcpnv(): SalesMetricV2 {
  const id = "ecpnv";
  const name = "eCPNV";

  return {
    id,
    name,
    fullName: name,
    description:
      "Effective cost per new visit - calculated as Spend ÷ New Visits",
    category: "northbeam",
    positiveTrend: "down",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeCacOrderSubscription({
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "cacOrderSubscription";

  return {
    id,
    name: isDashboardV2 ? "Subscription CPO" : "Subscription CAC",
    fullName:
      "Subscription CPO / All" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description: isDashboardV2
      ? "Includes all subscription categories"
      : "Cost to acquire a customer - calculated as Spend ÷ Txns",
    category: "northbeam",
    positiveTrend: "down",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeCacOrderSubscriptionFirstTime({
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "cacOrderSubscriptionFirstTime";

  return {
    id,
    name: isDashboardV2
      ? "Subscription CPO (New)"
      : "Subscription CAC (1st time)",
    fullName:
      "Subscription CPO / New" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description: isDashboardV2
      ? "Includes order tags you've mapped to the 'first time' category"
      : "Includes order tags you've mapped to the 'first time' category",
    category: "northbeam",
    positiveTrend: "down",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeCacOrderSubscriptionRecurring({
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "cacOrderSubscriptionRecurring";

  return {
    id,
    name: isDashboardV2
      ? "Subscription CPO (Recurring)"
      : "Subscription CAC (Recurring)",
    fullName:
      "Subscription CPO / Recurring" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description: isDashboardV2
      ? "Includes order tags you've mapped to the 'recurring' category"
      : "Includes order tags you've mapped to the 'recurring' category",
    category: "northbeam",
    positiveTrend: "down",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeCacOrderSubscriptionOther({
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "cacOrderSubscriptionOther";

  return {
    id,
    name: isDashboardV2
      ? "Subscription CPO (Other)"
      : "Subscription CAC (Other)",
    fullName:
      "Subscription CPO / Other" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description: isDashboardV2
      ? "Includes all order tags that were mapped to 'other', or could not be mapped to " +
        "'recurring' or 'first time'"
      : "Includes all order tags that were mapped to 'other', or could not be mapped to " +
        "'recurring' or 'first time'",
    category: "northbeam",
    positiveTrend: "down",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeCacOrderSubscriptionLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "cacOrderSubscriptionLtv";

  return {
    id,
    name: isDashboardV2 ? "Subscription CPO (∞)" : "LTV Subscription CAC",
    fullName: "Subscription CPO / All (∞)",
    description: "Includes all subscription categories",
    category: "northbeam",
    positiveTrend: "down",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeCacOrderSubscriptionFirstTimeLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "cacOrderSubscriptionFirstTimeLtv";

  return {
    id,
    name: isDashboardV2
      ? "Subscription CPO (∞, New)"
      : "LTV Subscription CAC (1st time)",
    fullName: "Subscription CPO / New (∞)",
    description:
      "Includes order tags you've mapped to the 'first time' category",
    category: "northbeam",
    positiveTrend: "down",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeCacOrderSubscriptionRecurringLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "cacOrderSubscriptionRecurringLtv";

  return {
    id,
    name: isDashboardV2
      ? "Subscription CPO (∞, Recurring)"
      : "LTV Subscription CAC (Recurring)",
    fullName: "Subscription CPO / Recurring (∞)",
    description:
      "Includes order tags you've mapped to the 'recurring' category",
    category: "northbeam",
    positiveTrend: "down",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeCacOrderSubscriptionOtherLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "cacOrderSubscriptionOtherLtv";

  return {
    id,
    name: isDashboardV2
      ? "Subscription CPO (∞, Other)"
      : "LTV Subscription CAC (Other)",
    fullName: "Subscription CPO / Other (∞)",
    description:
      "Includes all order tags that were mapped to 'other', or could not be mapped to " +
      "'recurring' or 'first time'",
    category: "northbeam",
    positiveTrend: "down",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeCPV({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "cpv";

  return {
    id,
    name: "CPV",
    fullName:
      "CPV / All" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description: "(Spend) / (Attributed Visitors)",
    category: "northbeam",
    positiveTrend: "down",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isDashboardV2: true,
  };
}

export function makeCPVFt({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "cpvFt";

  return {
    id,
    name: "CPV (New)",
    fullName:
      "CPV / New" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "(Spend associated with first click) / (Attributed 1st time visitor)",
    category: "northbeam",
    positiveTrend: "down",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isDashboardV2: true,
  };
}

export function makeCPVRtn({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "cpvRtn";

  return {
    id,
    name: "CPV (Returning)",
    fullName:
      "CPV / Ret" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "(Spend associated with all clicks except first click) / (Attributed returning visitor)",
    category: "northbeam",
    positiveTrend: "down",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isDashboardV2: true,
  };
}

export function makeRPV({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "rpv";

  return {
    id,
    name: "RPV",
    fullName:
      "RPV / All" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description: "(All Revenue) / (All Visitors)",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isDashboardV2: true,
  };
}

export function makeRPVFt({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "rpvFt";

  return {
    id,
    name: "RPV (New)",
    fullName:
      "RPV / New" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description: "(First Time Revenue) / (First Time Visitors)",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isDashboardV2: true,
  };
}

export function makeRPVRtn({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "rpvRtn";

  return {
    id,
    name: "RPV (Returning)",
    fullName:
      "RPV / Ret" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description: "(Returning Revenue) / (Returning Visitors)",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isDashboardV2: true,
  };
}

export function makeNewCac({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "newCac";

  return {
    id,
    name: "CAC",
    fullName:
      "CAC / All" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "Cost to acquire a customer - calculated as All Spend ÷ All Customers",
    category: "northbeam",
    positiveTrend: "down",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isDashboardV2: true,
  };
}

export function makeNewCacFt({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "newCacFt";

  return {
    id,
    name: "CAC (New)",
    fullName:
      "CAC / New" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "Cost to acquire a new customer - calculated as All Spend ÷ New Customers",
    category: "northbeam",
    positiveTrend: "down",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isDashboardV2: true,
  };
}

export function makeNewCacRtn({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "newCacRtn";

  return {
    id,
    name: "CAC (Returning)",
    fullName:
      "CAC / Ret" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "Cost to acquire a returning customer - calculated as All Spend ÷ Returning Customers",
    category: "northbeam",
    positiveTrend: "down",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isDashboardV2: true,
  };
}

export function makePercentageNewVisitors(): SalesMetricV2 {
  const id = "percentageNewVisitors";
  const name = "% New Visitors";

  return {
    id,
    name,
    fullName: name,
    description:
      "Percentage of visitors to your website that are new visitors, as determined by " +
      "the Northbeam Pixel",
    category: "northbeam",
    positiveTrend: "up",
    format: "percentage",
    getValue(metrics) {
      return metrics[id];
    },
    isDashboardV2: true,
  };
}

export function makePercentageNewCustomers(): SalesMetricV2 {
  const id = "percentageNewCustomers";
  const name = "% New Customers";

  return {
    id,
    name,
    fullName: name,
    description:
      "Percentage of customers to your website that are new customers, as determined by " +
      "the Northbeam Pixel",
    category: "northbeam",
    positiveTrend: "up",
    format: "percentage",
    getValue(metrics) {
      return metrics[id];
    },
    isDashboardV2: true,
  };
}
